'use es6';

import { fromJS } from 'immutable';
import { getDefaultOptions } from '../../../highcharts/options/defaultOptions';
import { getLegendPositionOptions } from '../../../highcharts/options/legendOptions';
import { getAxisOptions } from './axisOptions';
import { getBubbleLegendOptions } from './bubbleLegendOptions';
import { getSeriesOptions } from './seriesOptions';
import { getTooltipOptions } from './tooltipOptions';
import { ACCESSIBILITY_DATE_FORMATS } from 'reporting-data/constants/dateFormats';
import { formatDate } from '../../../formatters/propertyFormatter';
import { getMultiMeasureSeriesOptions } from './multiMeasureSeriesOptions';
import { doMeasuresHaveDifferentFormatting, isSnowflakeMultiMeasureReport, isMultipleLayersWithSingleLinearAxis } from '../xyUtils';
const generateTooltip = (chart, layers) => {
  if (chart.getIn(['options', 'tooltipDisabled'])) {
    return {
      enabled: false
    };
  }
  const tooltipFormatter = chart.getIn(['options', 'tooltipFormatter']);
  return tooltipFormatter ? {
    formatter: tooltipFormatter
  } : getTooltipOptions({
    chart,
    layers
  });
};
export const buildChartOptions = ({
  chart,
  layers
}, reportingThemes) => {
  const chartType = chart.get('options') && chart.getIn(['options', 'type'], 'column').toLowerCase();
  const dataLabels = chart.getIn(['options', 'dataLabels']);
  const stackLabels = chart.getIn(['options', 'stackLabels']);
  const seriesStackingType = chart.getIn(['series', 0, 'stacking']);
  const hasMultipleLinearAxes = isMultipleLayersWithSingleLinearAxis(layers);
  const inverted = chart.getIn(['axes', 'inverted'], false);
  const bottomLegend = chart.getIn(['options', 'bottomLegend']);
  const isDatetime = chart.getIn(['axes', 'xAxis', 0, 'property', 'type']) === 'datetime';
  const dateResolution = chart.getIn(['options', 'dateResolution']);
  const {
    legend: legendOptions = {},
    chart: legendOffsets = {}
  } = getLegendPositionOptions(chart.getIn(['options', 'legendPosition']), chart.getIn(['options', 'legendWidth']));
  const isSnowflakeMultiMeasure = isSnowflakeMultiMeasureReport(layers);
  const multiMeasuresHaveDifferentFormatting = doMeasuresHaveDifferentFormatting(layers);
  let chartOptions = fromJS({
    chart: Object.assign({
      chartType,
      inverted
    }, bottomLegend ? {} : legendOffsets),
    title: chart.getIn(['options', 'title']),
    plotOptions: {
      series: {
        dataLabels: Object.assign({
          enabled: dataLabels
        }, hasMultipleLinearAxes ? {
          allowOverlap: true
        } : {}),
        states: chart.getIn(['options', 'disableSeriesHighlighting']) ? {
          hover: {
            enabled: false
          },
          inactive: {
            enabled: false
          }
        } : {}
      }
    },
    legend: Object.assign({
      enabled: chart.getIn(['options', 'showLegend']),
      bubbleLegend: getBubbleLegendOptions({
        chart,
        layers
      })
    }, bottomLegend ? {
      verticalAlign: 'bottom',
      layout: 'horizontal',
      floating: false,
      navigation: {
        enabled: false
      },
      y: 20
    } : Object.assign({}, legendOptions)),
    colors: chart.getIn(['options', 'colors']) || [],
    xAxis: getAxisOptions(chart.getIn(['axes', 'xAxis']), inverted, stackLabels),
    yAxis: getAxisOptions(chart.getIn(['axes', 'yAxis']), inverted, isSnowflakeMultiMeasure ? false : stackLabels, multiMeasuresHaveDifferentFormatting, seriesStackingType),
    series: isSnowflakeMultiMeasure ? getMultiMeasureSeriesOptions({
      chart,
      layers,
      inverted,
      stackLabels,
      dataLabels
    }) : getSeriesOptions({
      chart,
      layers,
      inverted
    }),
    tooltip: generateTooltip(chart, layers),
    accessibility: {
      point: {
        descriptionFormatter: point => {
          const seriesOptions = point.series.options;
          if (point.isNull || seriesOptions.formatNumber === undefined) {
            return false;
          }
          const numValue = seriesOptions.formatNumber(point.y);
          const seriesHasDates = (isDatetime || seriesOptions.seriesDates !== undefined) && dateResolution !== undefined;
          let category = point.category;
          if (seriesHasDates) {
            category = formatDate(seriesOptions.seriesDates[point.index], {
              format: ACCESSIBILITY_DATE_FORMATS[dateResolution],
              method: 'userTz'
            });
          } else if (point.category === point.index) {
            category = '';
          }
          return `${category}, ${numValue}`;
        }
      }
    }
  });
  if (!chart.getIn(['options', 'disableZoom'])) {
    chartOptions = chartOptions.setIn(['chart', 'zoomType'], 'x');
  }
  return getDefaultOptions({
    reportingThemes
  }).mergeDeep(chartOptions);
};